import { Flex, HStack } from '@chakra-ui/react';
import {
  Button,
  gridConfig,
  Menu,
  TextLink,
} from '@time-line/components/common';
import { useTheme } from '@time-line/components/theme';
import { ageOptions, ratingOptions } from 'components/reviews/constants';
import type { JunipProduct, JunipReviewRating } from 'types/reviews';

const { gridPxClamp } = gridConfig;

export type FilterNames = 'product' | 'rating' | 'age' | 'withMedia';

interface ReviewFiltersProps {
  productList: JunipProduct[];
  filtersToShow: FilterNames[];
  product?: string;
  rating?: JunipReviewRating;
  age?: string | null;
  withMedia?: boolean;
  onProductChange: (product: string | null) => void;
  onRatingChange: (rating: string | null) => void;
  onAgeChange: (age: string | null) => void;
  onWithMediaChange: (withMedia: boolean) => void;
  onResetFilters: () => void;
}

export function ReviewFilters({
  productList,
  filtersToShow,
  product,
  rating,
  age,
  withMedia,
  onProductChange,
  onRatingChange,
  onAgeChange,
  onWithMediaChange,
  onResetFilters,
}: ReviewFiltersProps) {
  const theme = useTheme();

  const hasFiltersApplied = product || rating || age || withMedia;

  return (
    <Flex
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
      gap="m"
      pb="m"
      width={`calc(calc(100vw - (100vw - 100%)) + ${gridPxClamp})`}
      overflowX="auto"
    >
      <Flex
        flexWrap={{ base: 'nowrap', lg: 'wrap' }}
        gap="4xs"
        alignItems="center"
        ml={{ base: '-m', lg: '0' }}
        pl={{ base: 'm', lg: '0' }}
        pr={gridPxClamp}
        sx={{
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {productList && filtersToShow.includes('product') && (
          <Menu
            size="sm"
            hasCaret
            menuItems={[{ title: 'All Products', id: -1 }, ...productList]}
            onSelectItem={(item) => {
              onProductChange(item.id > 0 ? item.id.toString() : null);
            }}
            renderButton={(item) =>
              product && item.title ? item.title : 'All Products'
            }
            renderItem={(item) =>
              item.id > 0 && item.title ? item.title : 'All'
            }
            state={product ? 'selected' : 'default'}
          />
        )}
        {filtersToShow.includes('rating') && (
          <Menu
            size="sm"
            hasCaret
            menuItems={ratingOptions}
            onSelectItem={(item) => {
              onRatingChange(item.value ? item.value.toString() : null);
            }}
            renderButton={() =>
              rating
                ? `${rating} star${Number(rating) > 1 ? 's' : ''}`
                : 'All ratings'
            }
            renderItem={(item) =>
              item.value
                ? `${item.value} star${Number(item.value) > 1 ? 's' : ''}`
                : 'All'
            }
            state={rating ? 'selected' : 'default'}
          />
        )}
        {filtersToShow.includes('age') && (
          <Menu
            size="sm"
            hasCaret
            menuItems={ageOptions}
            onSelectItem={(item) => {
              onAgeChange(item.value);
            }}
            renderButton={() =>
              age
                ? (ageOptions.find((item) => item.value === age)?.label ??
                  'All ages')
                : 'All ages'
            }
            renderItem={(item) => item.label}
            state={age ? 'selected' : 'default'}
          />
        )}
        {filtersToShow.includes('withMedia') && (
          <Button
            variant={withMedia ? 'primary' : 'outline'}
            isRound
            size="sm"
            width="auto"
            flexShrink={0}
            onClick={() => onWithMediaChange(!withMedia)}
            borderColor="blackAlpha.250"
            sx={theme.components.Text.sizes.overline}
            py="4xs"
            h="38px"
          >
            With media
          </Button>
        )}
      </Flex>
      {hasFiltersApplied && (
        <HStack alignItems="center" width={{ base: '100%', lg: 'auto' }}>
          <TextLink
            as="button"
            type="button"
            variant="underline"
            textProps={{ size: 'xs' }}
            onClick={onResetFilters}
            mr={gridPxClamp}
            beforeIconName="Close"
          >
            Reset filters
          </TextLink>
        </HStack>
      )}
    </Flex>
  );
}
